import { useState, useEffect, useCallback } from 'react';
import { GetQuestionnairesResponses } from '../services/fhir/patientsummary/questionnaireResponse/GetQuestionnaireResponse';

interface UseQuestionnaireResponsesProps {
    patientId?: string;
    tag?: string | string[];
}

interface QuestionnaireResponse {
    id: string;
    resource: {
        id: string;
        resourceType: string;
        questionnaire: string;
        status: string;
        authored: string;
        subject: {
            reference: string;
        };
        author: {
            reference: string;
        };
        meta?: {
            tag?: {
                system: string;
                code: string;
                display: string;
            }[];
        };
        item?: any[];
    };
}

interface UseQuestionnaireResponsesReturn {
    responses: QuestionnaireResponse[];
    loading: boolean;
    error: string | null;
    fetchResponses: () => Promise<void>;
    getResponsesByQuestionnaire: (questionnaireId: string) => QuestionnaireResponse[];
}

export function useQuestionnaireResponses({
    patientId,
    tag,
}: UseQuestionnaireResponsesProps = {}): UseQuestionnaireResponsesReturn {
    const [responses, setResponses] = useState<QuestionnaireResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchResponses = useCallback(async () => {
        if (!patientId) return;

        setLoading(true);
        setError(null);

        try {
            const data = await GetQuestionnairesResponses({
                patient: patientId,
                tag
            });

            const responsesList = data?.entry?.map((entry: any) => ({
                id: entry.resource.id,
                resource: entry.resource,
            })) || [];

            setResponses(responsesList);
        } catch (err: any) {
            console.error('Erro ao buscar questionnaire responses:', err);
            setError(err?.message || 'Erro ao buscar respostas dos questionários');
        } finally {
            setLoading(false);
        }
    }, [patientId, tag]);

    const getResponsesByQuestionnaire = useCallback((questionnaireId: string) => {
        return responses.filter(response =>
            response.resource.questionnaire?.includes(questionnaireId)
        );
    }, [responses]);

    useEffect(() => {
        if (patientId) {
            fetchResponses();
        }
    }, [patientId, fetchResponses]);

    return {
        responses,
        loading,
        error,
        fetchResponses,
        getResponsesByQuestionnaire,
    };
}
